import { DATE_FORMAT, formatShortISO, parse } from '@eneco/common/date';
import { ApiError } from '@eneco/dc/client/ApiError';
import { createOrder } from '@eneco/dc/services/sales/EnecoInHouseService';
import { MachineConfig } from '@eneco/flows/types';
import { Products_Offers_V3_OfferResponseModel, Products_Orders_SwitchTypeResponse } from '@monorepo-types/dc';
import {
  ProductRequest,
  CreateOrderResponse,
  PersonRequest,
  DataEnvelopeOfCreateOrderRequest,
} from '@monorepo-types/dc/sales';
import { fromPromise, setup } from 'xstate';

import { FlowContextServiceGemak } from '../context';
import { selectCostDetailsByType } from '../helpers/dc';

interface OrderMachineContext {
  id?: string;
  orderTypes?: Array<Products_Orders_SwitchTypeResponse>;
  errorMessage?: string;
}

type OrderEvent =
  | {
      type: 'SEND_ORDER';
      config: MachineConfig;
      values: FlowContextServiceGemak;
      offer: Products_Offers_V3_OfferResponseModel;
    }
  | { type: 'NOT_AUTHENTICATED' };

// isEmailOptedIn is not generated by auto type generation
// TODO: remove this after the type-generation issue is fully resolved
interface PersonRequestWithEmailOptedIn extends PersonRequest {
  isEmailOptedIn: boolean;
}

interface InputType {
  event: OrderEvent;
}

interface OutputType {
  data?: CreateOrderResponse;
}

enum ProductCodeMaps {
  SO710 = 'ESBCV', // basis
  SO720 = 'ESECV', // extra
  SO730 = 'ESCCV', // compleet
}

const constructProductsList = (
  flowData: FlowContextServiceGemak,
  offersData: Products_Offers_V3_OfferResponseModel,
): Array<ProductRequest> => {
  const startDate = formatShortISO(new Date());
  const products: Array<ProductRequest> = [];
  const kcOffer = offersData?.productCombinations?.find(o =>
    o.products?.some(p => p.marketingProductCode === flowData?.sgProduct),
  );

  const product: ProductRequest = {
    externalReference: '',
    code: ProductCodeMaps[kcOffer?.products?.[0]?.marketingProductCode as keyof typeof ProductCodeMaps],
    quantity: 1,
    price: selectCostDetailsByType('monthlyCosts', kcOffer?.costDetails)?.[
      flowData.isSME ? 'vatExcluded' : 'vatIncluded'
    ],
    startDate,
    asset: {
      externalReference: '',
      brand: flowData.kcMerk || '',
      deviceProperties: [
        { key: 'Is de ketel van jou?', value: 'ja' },
        { key: 'Hangt de ketel in een ruimte zonder asbest?', value: 'ja' },
        { key: 'Welk merk ketel betreft het?', value: flowData.kcMerk || '' },
        { key: 'Is je ketel goed bereikbaar?', value: 'ja' },
      ],
    },
  };

  products.push(product);
  return products;
};

const sendOrderService = (
  config: MachineConfig,
  flowData: FlowContextServiceGemak,
  offersData: Products_Offers_V3_OfferResponseModel,
): Promise<OutputType> =>
  new Promise((resolve, reject) => {
    const customer: PersonRequestWithEmailOptedIn = {
      externalReference: '',
      firstName: flowData.firstName ?? '',
      lastName: flowData.surname ?? '',
      isEmailOptedIn: Boolean(flowData.C1),
      lastNamePreposition: flowData.surnamePreposition,
      gender: flowData.salutation && flowData.salutation === 'dhr' ? 'Male' : 'Female',
      phone: flowData.phoneNumber?.replace(/[^\d]/g, '') ?? '',
      secondaryPhone: flowData.phoneNumber?.replace(/[^\d]/g, '') ?? '',
      email: flowData.emailAddress ?? '',
      birthDate: flowData.birthdate && formatShortISO(parse(flowData.birthdate, DATE_FORMAT)),
      iban: flowData.iban?.replace(/\s/g, ''),
      isDirectDebit: true,
      address: {
        externalReference: '',
        street: flowData.street ?? '',
        houseNumber: flowData.houseNumber as number,
        houseNumberAddition: flowData.houseNumberSuffix,
        postalCode: flowData.postalCode ?? '',
        city: flowData.city ?? '',
      },
    };

    const requestBody: DataEnvelopeOfCreateOrderRequest = {
      data: {
        externalReference: flowData.basketId,
        customer,
        products: constructProductsList(flowData, offersData),
      },
    };

    return createOrder({ requestBody })
      .then(response => {
        resolve({ data: response });
      })
      .catch((error: ApiError) => {
        reject(new Error(error?.message));
      });
  });

export const orderMachine = setup({
  types: {} as {
    context: OrderMachineContext;
    events: OrderEvent;
    actors: {
      fetchData: {
        input: InputType;
        output: OutputType;
      };
    };
  },
  actions: {
    clearErrorMessage: () => ({ errorMessage: undefined as undefined }),
  },
  actors: {
    sendOrder: fromPromise<OutputType, InputType>(({ input }) => {
      const { event } = input;
      if (event.type !== 'SEND_ORDER') {
        throw new Error('event type should be SEND_ORDER');
      }
      return sendOrderService(event.config, event.values, event.offer);
    }),
  },
}).createMachine({
  id: 'order',
  initial: 'IDLE',
  context: {},
  on: {
    SEND_ORDER: { target: '.FETCHING' },
  },
  states: {
    IDLE: {},
    FETCHING: {
      invoke: {
        src: 'sendOrder',
        input: ({ event }) => ({ event }),
        onDone: {
          target: 'SUCCESS',
          actions: ({ event, context }) => {
            return { ...context, ...event.output.data };
          },
        },
        onError: {
          target: 'ERROR',
          actions: [
            ({ event, context }) => {
              const error = event.error as Error;
              const type = error?.message === 'Unauthorized' ? 'NOT_AUTHENTICATED' : '';
              if (type) {
                return { type } as OrderEvent;
              }
              return { type: '', errorMessage: error?.message };
            },
          ],
        },
      },
    },
    SUCCESS: {
      entry: 'clearErrorMessage',
      type: 'final',
    },
    ERROR: {
      on: {
        NOT_AUTHENTICATED: { target: 'ERROR_NOT_AUTHENTICATED' },
      },
    },
    ERROR_NOT_AUTHENTICATED: {},
  },
});
